import { useState } from "react";
import { Download, Check, Loader2 } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { InnerBlock } from "@/components/InnerBlock";
import { InnerCTA } from "@/components/InnerCTA";
import { api } from "@/lib/api";
import { DOWNLOADS } from "@/data/downloads";

function DownloadCard({ d }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim() || !email.trim() || !consent) {
      setError("Name, email and consent are required.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", {
        source: `download-${d.slug}`,
        name,
        email,
        payload: {
          company,
          download: d.title,
          consent: true,
        },
      });
      setDone(true);
    } catch (err) {
      setError("Something went wrong. Please email support@wafa.om and we will send it to you.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id={d.slug}
      data-testid={`download-card-${d.slug}`}
      className="rounded-lg border border-wafa-deep/10 bg-white p-6 lg:p-8"
    >
      <div className="grid gap-8 lg:grid-cols-5">
        <div className="lg:col-span-3">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-wafa-primary">
            <Download className="h-4 w-4" /> Download
          </div>
          <h2 className="mt-3 font-heading text-2xl font-semibold text-wafa-deep sm:text-3xl">
            {d.title}
          </h2>
          <p className="mt-2 text-sm text-wafa-ink/70 lg:text-base">{d.subtitle}</p>
          <p className="mt-5 text-sm text-wafa-ink/85 lg:text-base">{d.description}</p>
          <ul className="mt-5 space-y-2">
            {d.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-wafa-ink/80 lg:text-base">
                <Check className="mt-1 h-4 w-4 shrink-0 text-wafa-primary" /> <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-2">
          {done ? (
            <div
              data-testid={`download-success-${d.slug}`}
              className="rounded-lg border border-wafa-primary/25 bg-wafa-cream/40 p-6"
            >
              <div className="flex items-center gap-2 text-sm font-semibold text-wafa-deep">
                <Check className="h-5 w-5 text-wafa-primary" /> Sent — check your inbox.
              </div>
              <p className="mt-3 text-sm text-wafa-ink/75">
                We have your details. The guide will land in your inbox shortly. If it does not arrive, email support@wafa.om and we will send it to you directly.
              </p>
              <a
                href={d.fileHref}
                data-testid={`download-file-${d.slug}`}
                className="mt-5 inline-flex items-center gap-2 rounded-md bg-wafa-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-wafa-deep"
              >
                Download now <Download className="h-4 w-4" />
              </a>
            </div>
          ) : (
            <form onSubmit={submit} data-testid={`download-form-${d.slug}`} className="rounded-lg border border-wafa-deep/10 bg-wafa-cream/40 p-5 lg:p-6">
              <div className="text-xs font-semibold uppercase tracking-widest text-wafa-primary">Get the guide</div>
              <div className="mt-3 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-wafa-deep" htmlFor={`${d.slug}-name`}>Full name</label>
                  <input
                    id={`${d.slug}-name`}
                    data-testid={`download-name-${d.slug}`}
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="mt-1 w-full rounded-md border border-wafa-deep/15 bg-white px-3 py-2 text-sm focus:border-wafa-primary focus:outline-none focus:ring-1 focus:ring-wafa-primary"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-wafa-deep" htmlFor={`${d.slug}-email`}>Work email</label>
                  <input
                    id={`${d.slug}-email`}
                    data-testid={`download-email-${d.slug}`}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-1 w-full rounded-md border border-wafa-deep/15 bg-white px-3 py-2 text-sm focus:border-wafa-primary focus:outline-none focus:ring-1 focus:ring-wafa-primary"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-wafa-deep" htmlFor={`${d.slug}-company`}>Company (optional)</label>
                  <input
                    id={`${d.slug}-company`}
                    data-testid={`download-company-${d.slug}`}
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    className="mt-1 w-full rounded-md border border-wafa-deep/15 bg-white px-3 py-2 text-sm focus:border-wafa-primary focus:outline-none focus:ring-1 focus:ring-wafa-primary"
                  />
                </div>
                <label className="flex items-start gap-2 text-xs text-wafa-ink/70">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    data-testid={`download-consent-${d.slug}`}
                    className="mt-0.5 h-4 w-4 rounded border-wafa-deep/25 text-wafa-primary focus:ring-wafa-primary"
                    required
                  />
                  <span>I agree to Wafa Solutions storing my name and email so it can send me this guide. I can withdraw consent at any time by emailing support@wafa.om.</span>
                </label>
                {error ? (
                  <div data-testid={`download-error-${d.slug}`} className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>
                ) : null}
                <button
                  type="submit"
                  data-testid={`download-submit-${d.slug}`}
                  disabled={loading}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-wafa-primary px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-wafa-deep disabled:opacity-70"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  Send me the guide
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

export default function Downloads() {
  return (
    <div data-testid="downloads-index">
      <Seo title="Downloads" description="Practical guides and templates from Wafa Solutions — PDPL, software briefs and delivery." />
      <PageHero
        eyebrow="Resources"
        h1="Guides you can put to work"
        intro="Three practical guides drawn from our own work. Give us your name and email and we will send you the file — nothing else, no drip campaign, no third-party sharing."
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "Resources", to: "/resources" }, { label: "Downloads" }]}
      />

      <InnerBlock tint="white">
        <div className="space-y-8">
          {DOWNLOADS.map((d) => (
            <DownloadCard key={d.slug} d={d} />
          ))}
        </div>
      </InnerBlock>

      <InnerCTA
        heading="Want something written for your team?"
        body="Tell us the question, and if the answer is genuinely useful to more people, we will write it and send it to you first."
      />
    </div>
  );
}
